import React from 'react';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { OpenInNewWindow } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { useFeaturedPlacementTelemetry } from '@apps/stays/AccommodationsConsole/FeaturedPlacementTelemetryContext';
import { withWindow } from '@shared/utils/withWindow';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName, getSelectedRedirectTravelOfferProvider } from '@shared/utils/extractHotelRedirects';
import { affiliateHandoffTelemetry } from '@shared/utils/formatting/affiliateHandoff';
import { otaTransferredPricingTelemetry } from '../otaTransferredPricing.telemetry';
import { useTranslation } from '@shared/core';
import { HotelSearchSource } from '@shared/constants/accommodations';

type HotelCTAProps = Readonly<{
  listing: AccommodationListingDTO;
  redirectUrl?: string;
  sourceName: HotelSearchSource;
  /** Optional background override (e.g. the cash-fund variant's mono14). Defaults to the primary intent. */
  backgroundColor?: string;
  /** Hover fill; without it the button does not react to hover. */
  hoverBackgroundColor?: string;
  /**
   * Size the button to its label and square off its corners, instead of a full-width mobile pill.
   *
   * For the cash-fund footer strip, where the button shares one row with the fund split at every
   * width. The default full-width pill is what every other mobile tile ships.
   */
  compact?: boolean;
  /**
   * Replace the default "See Availability" wording. For the featured-placement hero, whose design
   * names the partner the button lands on. Already-translated text — callers pass a `tq` result.
   */
  label?: string;
  /** Append the open-in-new-window glyph, for a CTA whose design marks it as leaving Joy. */
  showExternalLinkIcon?: boolean;
}>;

export const HotelCTA = ({ listing, redirectUrl, sourceName, backgroundColor, hoverBackgroundColor, compact, label, showExternalLinkIcon }: HotelCTAProps) => {
  const telemetry = useListingsPanelTelemetry();
  // Read, never resolved here: re-reading the experiment flag in a tile would fire one Amplitude
  // exposure per rendered CTA.
  const featuredPlacementTelemetry = useFeaturedPlacementTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const parseUrl = useSafeUrlParser();
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  const buttonTitle = label ?? (sourceName === HotelSearchSource.OTA ? tq`Book on Joy` : tq`See Availability`);

  const handleClick = useEventCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    telemetry.seeAvailabilityClicked({
      joyPlaceId: listing.joyPlaceId || '',
      hotelName: listing.name,
      isSponsored: listing.isSponsored ?? false,
      chain: listing.chain,
      isCoupleFavourite: listing.isFavorite ?? false,
      priceSource: listing.pricingInfo.source,
      travelOfferProvider: getSelectedRedirectTravelOfferProvider(listing.redirectMechanisms),
      ...otaTransferredPricingTelemetry(listing),
      ...featuredPlacementTelemetry,
    });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'HotelCTA.handleClick');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          joyPlaceId: listing.joyPlaceId || '',
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          destinationName: getSelectedRedirectTelemetryName(listing.redirectMechanisms),
          isSponsored: listing.isSponsored ?? false,
          chain: listing.chain,
          isCoupleFavourite: listing.isFavorite ?? false,
          // The hero's "Book on {partner}" button and every ordinary tile's CTA come through here,
          // which is where most sponsored clicks actually land.
          ...affiliateHandoffTelemetry(redirectUrl),
          ...otaTransferredPricingTelemetry(listing),
          ...mergedUtm,
          ...featuredPlacementTelemetry,
        });
      }
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  return (
    <Flex
      width={compact ? 'fit-content' : { _: '100%', md: 'fit-content' }}
      flexShrink={0}
    >
      <ButtonV2
        variant="solid"
        intent="primary"
        _hover={backgroundColor ? { backgroundColor: hoverBackgroundColor ?? backgroundColor } : { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonPrimaryHover' } }}
        backgroundColor={backgroundColor}
        height={compact ? '40px' : { _: '48px', md: '40px' }}
        width="100%"
        onClick={handleClick}
        shape="rounded"
        {...(compact && { borderRadius: pxToRem(8), paddingX: 5, whiteSpace: 'nowrap' })}
      >
        {buttonTitle}
        {showExternalLinkIcon && (
          <OpenInNewWindow
            size={16}
            marginLeft={pxToRem(6)}
          />
        )}
      </ButtonV2>
    </Flex>
  );
};
